/* ISS Siding estimate editor — single-tier, single-price-column layout.

   Sections come from /api/iss/catalog. Estimate persistence reuses the
   existing `lines[]` field on the Estimate doc, tagged with `tab="iss"`
   so the rows live alongside any siding/windows tabs if a contractor
   ever cross-references one job across workspaces.

   This page intentionally does NOT use the heavy `useEstimate` hook
   used by siding/windows — it has its own minimal state + autosave
   because the data shape is simpler. */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChevronDown, ChevronRight, Loader2, Lightbulb, Save, Upload, FileText, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import writeThrough from "@/lib/write_through";
import {
  vinylSidingColorGroupsForEstimate,
  accessoryColorGroupsForEstimate,
  ASCEND_COLORS,
  SHAKE_COLOR_GROUPS,
  BOARD_BATTEN_COLOR_GROUPS,
  SOFFIT_COLOR_GROUPS,
  GUTTER_COLORS,
  WINDOW_WRAP_COLORS,
} from "@/lib/colorOptions";
import { buildMaterialListHtml, materialListFilename } from "@/lib/materialList";
import { gateBlockMessage } from "@/lib/gateMessages";
import { useCompany } from "@/lib/company";
import { useBranding } from "@/lib/branding";
import { useLang } from "@/lib/i18n";
import { recomputeWasteQtys, bakeWasteIntoLines } from "@/lib/wasteLogic";
import { isValidEmail, isValidPhone, formatPhoneUS } from "@/lib/validate";
import { useT } from "@/lib/i18n";
import QuoteModal from "@/components/QuoteModal";
import ISSHoverImportButton from "@/components/estimate/ISSHoverImportButton";
import AIMeasureButton from "@/components/estimate/AIMeasureButton";
import BlueprintMeasureButton from "@/components/estimate/BlueprintMeasureButton";
import { Printer, Download, ClipboardList } from "lucide-react";
import { NO_AUTOFILL } from "@/lib/noAutofill";

const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;

// Master-catalog ADOPT (2026-07-18): the sheet's section split restored
// "Misc. Labor Only" + "Misc." — all three misc sections keep the per-row
// Mat $ override they carried while merged (feature parity, no regression).
const MISC_OVERRIDE_SECTIONS = new Set([
  "Misc. Labor and Material",
  "Misc. Labor Only",
  "Misc.",
]);

// Iter 78z++++ — Local copy of the JobInfoPanel ToolTile so ISS quotes
// render the same 3-column measurement-tools row as siding/windows
// estimates without dragging the heavy JobInfoPanel (and its
// useEstimate hook) into the ISS page.
function ISSToolTile({ icon: Icon, label, children, testid, accent = "#7C3AED" }) {
  return (
    <div
      className="border border-[var(--border)] bg-[var(--surface)] p-3 flex flex-col gap-2 min-w-0"
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
        <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--ink-2)] truncate">
          {label}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 items-start">{children}</div>
    </div>
  );
}

export default function ISSEstimateEditor() {
  const t = useT();
  // Iter 79j.49 — Per-field touched state for soft validation
  // (email + cell phone). Warnings only appear after blur.
  const [touched, setTouched] = useState({});
  const markTouched = (name) => setTouched((prev) => (prev[name] ? prev : { ...prev, [name]: true }));
  const { id } = useParams();
  const nav = useNavigate();
  const [est, setEst] = useState(null);
  const [catalog, setCatalog] = useState({ sections: [] });
  const [loading, setLoading] = useState(true);
  const [openSections, setOpenSections] = useState({});
  const [saving, setSaving] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const { company } = useCompany();
  const branding = useBranding();
  const { lang } = useLang();

  // Load estimate + catalog in parallel.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get(`/estimates/${id}`),
      api.get(`/iss/catalog`),
    ])
      .then(([estRes, catRes]) => {
        if (!alive) return;
        setEst(estRes.data);
        setCatalog(catRes.data);
      })
      .catch((e) => alive && toast.error(formatApiError(e?.response?.data?.detail)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id]);

  // Merge catalog × estimate.lines (tab=iss) into a render-ready map.
  // Saved qtys are preserved; un-touched catalog items render as qty=0.
  const lineByKey = useMemo(() => {
    const map = new Map();
    for (const l of est?.lines || []) {
      if ((l.tab || "") === "iss") {
        map.set(`${l.section}::${l.name}`, l);
      }
    }
    return map;
  }, [est?.lines]);

  // Persist qty changes (debounced).
  const userEdits = useRef(0);
  const savedUpTo = useRef(0);
  const savingNow = useRef(false);
  const flush = useCallback(async ({ force = false } = {}) => {
    if (!est) return;
    if (savingNow.current) return;
    if (!force && userEdits.current <= savedUpTo.current) return;
    const target = Math.max(userEdits.current, savedUpTo.current + 1);
    savingNow.current = true;
    setSaving(true);
    try {
      const payload = { ...est };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.user_id;
      delete payload.company_id;
      delete payload.totals;
      delete payload.estimate_number;
      const { data } = await api.put(`/estimates/${id}`, payload);
      savedUpTo.current = target;
      userEdits.current = target;
      setEst((cur) => ({ ...(cur || {}), ...data, lines: data.lines || [] }));
      if (force) toast.success("Estimate saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      savingNow.current = false;
      setSaving(false);
    }
  }, [est, id]);
  useEffect(() => {
    if (userEdits.current === 0) return;
    const t = setTimeout(flush, 1500);
    return () => clearTimeout(t);
  }, [est, flush]);

  const setQty = (section, name, unit, price, qty) => {
    const key = `${section}::${name}`;
    setEst((prev) => {
      if (!prev) return prev;
      const lines = [...(prev.lines || [])];
      const idx = lines.findIndex(
        (l) => (l.tab || "") === "iss" && l.section === section && l.name === name
      );
      if (idx >= 0) {
        lines[idx] = { ...lines[idx], qty: Number(qty) || 0, unit, mat: price, lab: 0, tab: "iss" };
      } else {
        lines.push({
          tab: "iss",
          section,
          name,
          unit,
          qty: Number(qty) || 0,
          mat: price,
          lab: 0,
        });
      }
      return { ...prev, lines };
    });
    userEdits.current += 1;
  };

  const updateField = (field, value) => {
    setEst((prev) => {
      if (!prev) return prev;
      let next = { ...prev, [field]: value };
      // Iter 78 — Waste % change recomputes line qtys for any line that
      // came from a HOVER import (carries raw_qty). Mirrors SettingsRow.
      if (field === "waste_pct") {
        next = { ...next, lines: recomputeWasteQtys(prev.lines, value) };
      }
      return next;
    });
    userEdits.current += 1;
  };

  // Apply HOVER-derived ISS rows: upsert each {section,name,unit,qty} into
  // est.lines tagged with tab="iss". The catalog price is looked up from
  // the loaded catalog so the row carries a real unit cost. Saves to the
  // backend immediately (not via the debounced autosave) so the imported
  // data is persisted before the modal closes.
  const applyHoverLines = useCallback(async (rows) => {
    if (!rows?.length) return;
    const priceMap = new Map();
    for (const sec of catalog.sections || []) {
      for (const it of sec.items) {
        priceMap.set(`${sec.title}::${it.name}`, it.price);
      }
    }
    const current = est;
    if (!current) return;
    // Iter 78 — bake current waste% into qty for cut-prone items
    // before merging, mirroring HOVER/Blueprint behavior on siding.
    const wastePct = Number(current?.waste_pct) || 0;
    const wastedRows = bakeWasteIntoLines(
      rows.map((r) => ({ ...r, tab: "iss" })),
      wastePct
    );
    const lines = [...(current.lines || [])];
    for (const r of wastedRows) {
      const key = `${r.section}::${r.name}`;
      const price = Number(priceMap.get(key) || 0);
      const idx = lines.findIndex(
        (l) => (l.tab || "") === "iss" && l.section === r.section && l.name === r.name
      );
      if (idx >= 0) {
        lines[idx] = {
          ...lines[idx],
          qty: Number(r.qty) || 0,
          raw_qty: r.raw_qty ?? null,
          unit: r.unit,
          mat: price,
          lab: 0,
          tab: "iss",
        };
      } else {
        lines.push({
          tab: "iss",
          section: r.section,
          name: r.name,
          unit: r.unit,
          qty: Number(r.qty) || 0,
          raw_qty: r.raw_qty ?? null,
          mat: price,
          lab: 0,
        });
      }
    }
    // Optimistic local update so the UI repaints immediately.
    // 2026-08-11 send-4: routed through writeThrough so a refused PUT
    // (e.g. 423 on a protected estimate) rolls back the local lines +
    // section-open state, and surfaces the refusal in words.
    const prevLines = current?.lines;
    const prevOpenSections = openSections;
    // Immediate persist — bypass the debounced autosave so the data is
    // safe even if the contractor navigates away right after applying.
    setSaving(true);
    try {
      const payload = { ...current, lines };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.user_id;
      delete payload.company_id;
      delete payload.totals;
      delete payload.estimate_number;
      let respData = null;
      await writeThrough({
        applyOptimistic: () => {
          setEst((prev) => (prev ? { ...prev, lines } : prev));
          setOpenSections((prev) => {
            const next = { ...prev };
            for (const r of rows) next[r.section] = true;
            return next;
          });
        },
        rollback: () => {
          setEst((prev) => (prev ? { ...prev, lines: prevLines } : prev));
          setOpenSections(prevOpenSections);
        },
        apiCall: async () => {
          const resp = await api.put(`/estimates/${id}`, payload);
          respData = resp.data;
          return resp;
        },
        reconcileFrom: () => {
          if (respData) {
            setEst((cur) => ({ ...(cur || {}), ...respData, lines: respData.lines || [] }));
            savedUpTo.current = userEdits.current;
          }
        },
        refusalTestid: "iss-apply-refusal-chip",
      });
    } finally {
      setSaving(false);
    }
  }, [catalog, est, id, openSections]);

  const totals = useMemo(() => {
    let subTotal = 0;
    let sidingSub = 0;
    for (const sec of catalog.sections || []) {
      for (const it of sec.items) {
        const ln = lineByKey.get(`${sec.title}::${it.name}`);
        const qty = Number(ln?.qty) || 0;
        if (qty > 0) {
          const lineAmt = qty * Number(it.price || 0);
          subTotal += lineAmt;
          // Waste applies only to siding squares — the install section is
          // material-heavy and the rest is labor / hardware.
          if (sec.title === "Install Vinyl Siding") sidingSub += lineAmt;
        }
      }
    }
    const wastePct = Number(est?.waste_pct) || 0;
    const wasteAdd = sidingSub * (wastePct / 100);
    const base = subTotal + wasteAdd;
    const pct = Math.min(Number(est?.margin_pct) || 0, 99);
    const mode = est?.pricing_mode || "margin";
    const sell = mode === "markup" ? base * (1 + pct / 100) : pct >= 99 ? base : base / (1 - pct / 100);
    const profit = sell - base;
    return { subTotal, sidingSub, wasteAdd, base, sell, profit };
  }, [catalog, lineByKey, est?.waste_pct, est?.margin_pct, est?.pricing_mode]);
  const grandTotal = totals.sell;

  // Customer Quote — make sure latest changes are saved first.
  const handleOpenQuote = useCallback(async () => {
    await flush({ force: true });
    setShowQuote(true);
  }, [flush]);

  // Material List PDF — same backend route the siding flow uses.
  const handlePrintMaterials = useCallback(async () => {
    if (!est) return;
    await flush({ force: true });
    const html = buildMaterialListHtml({ estimate: est, company, branding, lang });
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/estimates/${id}/pdf`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ recipient_email: "noreply@noreply.com", html_quote: html, surface: "material_list" }),
        }
      );
      if (!res.ok) throw new Error(await gateBlockMessage(res, t));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = materialListFilename(est);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  }, [est, company, branding, lang, id, flush]);

  const handleExportCsv = useCallback(async () => {
    try {
      const res = await api.get(`/exports/estimates/${id}.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `estimate_${est?.estimate_number || id}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [id, est?.estimate_number]);

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-[var(--surface-muted)]">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--brand-text)]" />
      </main>
    );
  }
  if (!est) return null;

  return (
    <main className="min-h-screen bg-[var(--surface-muted)] pb-24">
      {/* Iter 78z++++ — Sticky black banner that mirrors the multi-tab
          StickyBar used on the Vinyl/Ascend page. Single "ISS QUOTE"
          tab-block on the right so contractors see the same shape
          across every estimate kind. */}
      <div className="sell-bar" data-testid="iss-sticky-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center gap-3 sm:gap-6">
          <button
            type="button"
            onClick={() => nav("/dashboard/iss")}
            className="text-white/70 hover:text-white"
            aria-label="Back"
            data-testid="iss-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-[180px]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Estimate</div>
            <div className="font-heading text-base sm:text-lg truncate">
              {est.customer_name || `Untitled · ${est.estimate_number || ""}`}
            </div>
          </div>
          <div className="flex items-stretch gap-2 sm:gap-3 flex-wrap">
            <div
              className="px-3 py-1 border-l border-[var(--brand)]"
              data-testid="iss-bar-totals"
            >
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--brand-text)]">
                ISS Quote
              </div>
              <div
                className="font-mono-num text-base sm:text-xl font-bold text-[var(--brand-text)]"
                data-testid="iss-grand-total"
              >
                {fmt(grandTotal)}
              </div>
              <div className="flex items-baseline gap-2 mt-0.5">
                <span className="text-[10px] text-white/50 font-mono-num">
                  Base {fmt(totals.base)}
                </span>
                <span
                  className="text-[10px] font-mono-num"
                  style={{ color: totals.profit >= 0 ? "#10B981" : "#F87171" }}
                >
                  + {fmt(totals.profit)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <section className="card p-5 sm:p-6 mb-6" data-testid="iss-job-info">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="section-tag">Job Information</div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold">
                ISS Quote · {est.estimate_number || "Draft"}
              </div>
            </div>
            {saving && (
              <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" />
                Saving…
              </div>
            )}
          </div>

          {/* Iter 78z++++ — Three equal-width measurement-tool tiles —
              same shape as JobInfoPanel on siding/windows estimates. */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="iss-measurement-tools-row">
            <ISSToolTile icon={Upload} label="HOVER PDF" accent="#09090B" testid="iss-tile-hover">
              <ISSHoverImportButton est={est} applyLines={applyHoverLines} />
            </ISSToolTile>
            {/* SEND-337 — Draw on Blueprints and Draw on Photos are UNLOCKED
                again on the ISS door (reverses SEND-334). The walk is shared;
                its totals route through buildISSLinesFromMeasurements, which
                folds corners + trim into the installed siding square and keeps
                soffit/fascia as the LF charge. No per-piece rows. */}
            <ISSToolTile icon={FileText} label="Draw on Blueprints" accent="#7C3AED" testid="iss-tile-blueprint">
              <BlueprintMeasureButton est={est} applyLines={applyHoverLines} />
            </ISSToolTile>
            {/* SEND-188 — the second mount names the door too, and it named
                it in hardcoded English, which a dictionary re-word would
                have missed. One door, one name, both languages. */}
            <ISSToolTile icon={Sparkles} label={t("jip.tile.aiPhoto")} accent="#7C3AED" testid="iss-tile-ai">
              <AIMeasureButton
                kind="iss"
                address={est?.address}
                overhangIn={est?.overhang_in ?? 12}
                estimateId={est?.id}
                onApply={async ({ measurements }) => {
                  const { buildISSLinesFromMeasurements } = await import(
                    "@/components/estimate/ISSHoverImportButton"
                  );
                  const rows = buildISSLinesFromMeasurements(measurements || {});
                  await applyHoverLines(rows);
                  // Persist masked-zones summary so the PDF / email can
                  // show "Materials excluded: brick wainscot (220 ft²)".
                  if (measurements?._photo_zones_summary) {
                    updateField("photo_zones_summary", measurements._photo_zones_summary);
                    updateField("photo_zones_deducted_sqft", measurements._photo_zones_deducted_sqft || 0);
                  }
                }}
              />
            </ISSToolTile>
          </div>
        </section>

        <div className="card p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold block mb-0.5">Customer</label>
              <input
                className="input h-9 text-sm w-full"
                value={est.customer_name || ""}
                onChange={(e) => updateField("customer_name", e.target.value)}
                {...NO_AUTOFILL}
                data-testid="iss-customer-name"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold block mb-0.5">Address</label>
              <input
                className="input h-9 text-sm w-full"
                value={est.address || ""}
                onChange={(e) => updateField("address", e.target.value)}
                {...NO_AUTOFILL}
                data-testid="iss-address"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold block mb-0.5">Date</label>
              <input
                type="date"
                className="input h-9 text-sm w-full"
                value={est.estimate_date || ""}
                onChange={(e) => updateField("estimate_date", e.target.value)}
                data-testid="iss-date"
              />
            </div>
            {/* Iter 79j.47 — Email + cell phone. Optional; the ISS
                editor's PUT payload spreads the whole estimate, so
                no whitelist change is needed for these to persist. */}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold block mb-0.5">Email</label>
              <input
                type="email"
                className="input h-9 text-sm w-full"
                value={est.customer_email || ""}
                onChange={(e) => updateField("customer_email", e.target.value)}
                onBlur={() => markTouched("customer_email")}
                placeholder={`${t("est.exampleLead")} name@example.com`}
                aria-invalid={touched.customer_email && !isValidEmail(est.customer_email)}
                aria-describedby={touched.customer_email && !isValidEmail(est.customer_email) ? "iss-customer-email-warn" : undefined}
                {...NO_AUTOFILL}
                data-testid="iss-customer-email"
              />
              {touched.customer_email && !isValidEmail(est.customer_email) && (
                <div
                  id="iss-customer-email-warn"
                  role="alert"
                  className="text-[11px] leading-tight mt-1 text-[var(--warning-text)]"
                  data-testid="iss-customer-email-warn"
                >
                  {t("est.warnEmail")}
                </div>
              )}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold block mb-0.5">Cell Phone</label>
              <input
                type="tel"
                className="input h-9 text-sm w-full"
                value={est.customer_phone || ""}
                onChange={(e) => updateField("customer_phone", e.target.value)}
                onBlur={(e) => {
                  markTouched("customer_phone");
                  const norm = formatPhoneUS(e.target.value);
                  if (norm !== (est.customer_phone || "")) updateField("customer_phone", norm);
                }}
                placeholder={`${t("est.exampleLead")} (412) 555-0100`}
                aria-invalid={touched.customer_phone && !isValidPhone(est.customer_phone)}
                aria-describedby={touched.customer_phone && !isValidPhone(est.customer_phone) ? "iss-customer-phone-warn" : undefined}
                {...NO_AUTOFILL}
                data-testid="iss-customer-phone"
              />
              {touched.customer_phone && !isValidPhone(est.customer_phone) && (
                <div
                  id="iss-customer-phone-warn"
                  role="alert"
                  className="text-[11px] leading-tight mt-1 text-[var(--warning-text)]"
                  data-testid="iss-customer-phone-warn"
                >
                  {t("est.warnPhone")}
                </div>
              )}
            </div>
          </div>

          {/* Scope of Work — same field as siding estimates (notes) so it
              flows through to the PDF/email like the rest of the app. */}
          <div className="mt-3">
            <label className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold block mb-0.5">
              Scope of Work
            </label>
            <textarea
              className="input text-sm w-full"
              rows="3"
              value={est.notes || ""}
              onChange={(e) => updateField("notes", e.target.value)}
              data-testid="iss-notes"
            />
          </div>

          {/* Material Colors — mirrors siding estimate Job Info palette so
              the supplier pulls the right color stock for ISS jobs too. */}
          <div className="mt-4 pt-3 border-t border-[var(--border)]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-bold mb-2">
              Material Colors
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold block mb-0.5">Siding</label>
                <select
                  className="input h-9 text-sm w-full"
                  value={est.siding_color || ""}
                  onChange={(e) => updateField("siding_color", e.target.value)}
                  data-testid="iss-color-siding"
                >
                  <option value="">— Select —</option>
                  {vinylSidingColorGroupsForEstimate(est.lines || []).map((g) => (
                    <optgroup key={g.label} label={g.label}>
                      {g.colors.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold block mb-0.5">Ascend</label>
                <select
                  className="input h-9 text-sm w-full"
                  value={est.ascend_color || ""}
                  onChange={(e) => updateField("ascend_color", e.target.value)}
                  data-testid="iss-color-ascend"
                >
                  <option value="">— Select —</option>
                  {ASCEND_COLORS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold block mb-0.5">Shake (Pelican Bay)</label>
                <select
                  className="input h-9 text-sm w-full"
                  value={est.shake_color || ""}
                  onChange={(e) => updateField("shake_color", e.target.value)}
                  data-testid="iss-color-shake"
                >
                  <option value="">— Select —</option>
                  {SHAKE_COLOR_GROUPS.map((g) => (
                    <optgroup key={g.label} label={g.label}>
                      {g.colors.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold block mb-0.5">Board &amp; Batten</label>
                <select
                  className="input h-9 text-sm w-full"
                  value={est.board_batten_color || ""}
                  onChange={(e) => updateField("board_batten_color", e.target.value)}
                  data-testid="iss-color-board-batten"
                >
                  <option value="">— Select —</option>
                  {BOARD_BATTEN_COLOR_GROUPS.map((g) => (
                    <optgroup key={g.label} label={g.label}>
                      {g.colors.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold block mb-0.5">Accessories</label>
                <select
                  className="input h-9 text-sm w-full"
                  value={est.accessories_color || ""}
                  onChange={(e) => updateField("accessories_color", e.target.value)}
                  data-testid="iss-color-accessories"
                >
                  <option value="">— Select —</option>
                  {accessoryColorGroupsForEstimate(est.lines || []).map((g) => (
                    <optgroup key={g.label} label={g.label}>
                      {g.colors.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold block mb-0.5">Outside Corner</label>
                <select
                  className="input h-9 text-sm w-full"
                  value={est.outside_corner_color || ""}
                  onChange={(e) => updateField("outside_corner_color", e.target.value)}
                  data-testid="iss-color-outside-corner"
                >
                  <option value="">— Select —</option>
                  {accessoryColorGroupsForEstimate(est.lines || []).map((g) => (
                    <optgroup key={g.label} label={g.label}>
                      {g.colors.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold block mb-0.5">Soffit / Fascia</label>
                <select
                  className="input h-9 text-sm w-full"
                  value={est.soffit_fascia_color || ""}
                  onChange={(e) => updateField("soffit_fascia_color", e.target.value)}
                  data-testid="iss-color-soffit-fascia"
                >
                  <option value="">— Select —</option>
                  {SOFFIT_COLOR_GROUPS.map((g) => (
                    <optgroup key={g.label} label={g.label}>
                      {g.colors.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold block mb-0.5">Window Wrap</label>
                <select
                  className="input h-9 text-sm w-full"
                  value={est.window_wrap_color || ""}
                  onChange={(e) => updateField("window_wrap_color", e.target.value)}
                  data-testid="iss-color-window-wrap"
                >
                  <option value="">— Select —</option>
                  {WINDOW_WRAP_COLORS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold block mb-0.5">Gutter</label>
                <select
                  className="input h-9 text-sm w-full"
                  value={est.gutter_color || ""}
                  onChange={(e) => updateField("gutter_color", e.target.value)}
                  data-testid="iss-color-gutter"
                >
                  <option value="">— Select —</option>
                  {GUTTER_COLORS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing controls — Waste Factor + Profit (no Tax for ISS). */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div className="card p-4" data-testid="iss-waste-card">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-bold mb-2">
              Waste Factor
            </div>
            <div className="flex items-baseline gap-2">
              <input
                className="input num h-9 text-sm w-24"
                type="number"
                step="0.5"
                min="0"
                value={est.waste_pct || 0}
                onChange={(e) => updateField("waste_pct", Number(e.target.value) || 0)}
                data-testid="iss-waste-pct"
              />
              <span className="text-sm text-[var(--ink-2)]">% on siding squares</span>
            </div>
            <p className="mt-2 text-[10px] uppercase tracking-wider text-[var(--muted)]">
              Applied to Install Vinyl Siding subtotal · ${totals.sidingSub.toFixed(2)} × {Number(est.waste_pct) || 0}% = ${totals.wasteAdd.toFixed(2)}
            </p>
            {/* Iter 45: soffit overhang for the PCS formula. */}
            <div className="mt-3 pt-3 border-t border-[var(--border)]">
              <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold mb-2">Eave Overhang</div>
              <div className="flex items-baseline gap-2">
                <input
                  className="input num h-9 text-sm w-24"
                  type="number"
                  step="1"
                  min="0"
                  value={est.overhang_in ?? 12}
                  onChange={(e) => updateField("overhang_in", Number(e.target.value) || 0)}
                  data-testid="iss-overhang-in"
                />
                <span className="text-sm text-[var(--ink-2)]">in · drives soffit piece count</span>
              </div>
            </div>
          </div>
          {(() => {
            const mode = est.pricing_mode || "margin";
            const isMargin = mode === "margin";
            const pct = Math.min(Number(est.margin_pct) || 0, 99);
            const mult = isMargin ? (pct >= 99 ? Infinity : 1 / (1 - pct / 100)) : 1 + pct / 100;
            return (
              <div className="card p-4" data-testid="iss-profit-card">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-bold">
                    Profit
                  </div>
                  <div
                    className="inline-flex border border-[var(--border)] rounded-sm overflow-hidden text-[10px] font-bold uppercase tracking-wider"
                    data-testid="iss-pricing-mode-toggle"
                  >
                    <button
                      type="button"
                      className={`px-3 py-1 transition ${
                        isMargin
                          ? "bg-[var(--bar-bg)] text-white"
                          : "bg-[var(--surface)] text-[var(--ink-2)] hover:bg-[var(--bg-app)]"
                      }`}
                      onClick={() => updateField("pricing_mode", "margin")}
                      data-testid="iss-pricing-mode-margin"
                    >
                      Margin
                    </button>
                    <button
                      type="button"
                      className={`px-3 py-1 transition border-l border-[var(--border)] ${
                        !isMargin
                          ? "bg-[var(--bar-bg)] text-white"
                          : "bg-[var(--surface)] text-[var(--ink-2)] hover:bg-[var(--bg-app)]"
                      }`}
                      onClick={() => updateField("pricing_mode", "markup")}
                      data-testid="iss-pricing-mode-markup"
                    >
                      Markup
                    </button>
                  </div>
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <input
                    className="input num h-9 text-sm w-24"
                    type="number"
                    step="1"
                    min="0"
                    max={isMargin ? 99 : undefined}
                    value={est.margin_pct || 0}
                    onChange={(e) => updateField("margin_pct", Number(e.target.value) || 0)}
                    data-testid="iss-margin-pct"
                  />
                  <span className="text-sm text-[var(--ink-2)]">
                    {isMargin ? "% profit margin" : "% markup on base"}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max={isMargin ? 95 : 100}
                  step="1"
                  value={est.margin_pct || 0}
                  onChange={(e) => updateField("margin_pct", Number(e.target.value) || 0)}
                  className="w-full accent-[var(--brand)]"
                  data-testid="iss-margin-slider"
                />
                <div className="mt-2 text-[11px] text-[var(--muted)] font-mono-num">
                  {isMargin ? (
                    <>Sell = Base ÷ (1 − {pct}%) = <span className="text-[var(--ink)] font-bold">×{Number.isFinite(mult) ? mult.toFixed(3) : "∞"}</span></>
                  ) : (
                    <>Sell = Base × (1 + {pct}%) = <span className="text-[var(--ink)] font-bold">×{mult.toFixed(3)}</span></>
                  )}
                </div>
              </div>
            );
          })()}
        </section>

        {(catalog.sections || []).map((sec) => {
          const isOpen = !!openSections[sec.title];
          // Iter 78z++++ — Header subtotal honors per-row Mat $
          // overrides on the merged "Misc. Labor and Material" section.
          // Master-catalog ADOPT (2026-07-18): the misc split restored
          // "Misc. Labor Only" + "Misc." — all three misc sections keep
          // the per-row Mat $ override they had while merged.
          const sectionIsMiscMat = MISC_OVERRIDE_SECTIONS.has(sec.title);
          const sectionTotal = sec.items.reduce((s, it) => {
            const ln = lineByKey.get(`${sec.title}::${it.name}`);
            const qty = Number(ln?.qty) || 0;
            const unit = sectionIsMiscMat && ln && ln.mat != null
              ? Number(ln.mat)
              : Number(it.price || 0);
            return s + qty * unit;
          }, 0);
          const filledCount = sec.items.filter((it) => {
            const ln = lineByKey.get(`${sec.title}::${it.name}`);
            return (Number(ln?.qty) || 0) > 0;
          }).length;
          // Common-but-still-unquoted count — matches the badge contractors
          // already know from the Siding section headers.
          const unfilledCommon = sec.items.filter((it) => {
            if (!it.tip) return false;
            const ln = lineByKey.get(`${sec.title}::${it.name}`);
            return (Number(ln?.qty) || 0) <= 0;
          }).length;
          return (
            <section key={sec.title} className="card mb-3" data-testid={`iss-section-${sec.title}`}>
              <button
                type="button"
                onClick={() =>
                  setOpenSections((p) => ({ ...p, [sec.title]: !p[sec.title] }))
                }
                className="w-full flex items-center justify-between px-4 md:px-5 py-3 border-b border-[var(--border)] bg-[var(--surface-muted)] hover:bg-[var(--bg-app)]"
                data-testid={`iss-section-toggle-${sec.title}`}
              >
                <div className="flex items-center gap-2">
                  {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <div className="section-tag text-left">{sec.title}</div>
                  {filledCount > 0 && (
                    <span className="bg-[var(--brand)] text-[var(--on-brand)] px-2 py-0.5 text-[10px] tracking-wider font-bold normal-case">
                      {filledCount}
                    </span>
                  )}
                  {unfilledCommon > 0 && (
                    <span
                      className="text-[10px] font-bold px-2 py-0.5 bg-[var(--hint-bg-2)] border border-[var(--hint-line)] text-[var(--hint-ink-2)] flex items-center gap-1"
                      title="Commonly-needed items in this section haven't been quoted yet"
                      data-testid={`iss-common-flag-${sec.title}`}
                    >
                      <Lightbulb className="w-3 h-3" />
                      {unfilledCommon}
                    </span>
                  )}
                </div>
                <span className="font-mono-num text-sm text-[var(--ink-2)]">{fmt(sectionTotal)}</span>
              </button>
              {isOpen && (
                <div className="divide-y divide-[#E4E4E7]">
                  {sec.items.map((it) => {
                    const ln = lineByKey.get(`${sec.title}::${it.name}`);
                    const qty = Number(ln?.qty) || 0;
                    // Iter 78z++++ — Mat $ override for the merged
                    // "Misc. Labor and Material" section. The contractor
                    // can punch in a per-job material cost; the row's
                    // line total + section subtotal both reflect the
                    // override. Stored on the line as `mat` (ISS lines
                    // already use `mat` to carry the unit price with
                    // `lab` always 0).
                    const isMiscMat = MISC_OVERRIDE_SECTIONS.has(sec.title);
                    const effectivePrice = isMiscMat && ln && ln.mat != null
                      ? Number(ln.mat)
                      : Number(it.price || 0);
                    const lineTotal = qty * effectivePrice;
                    return (
                      <div
                        key={it.name}
                        className={`px-4 md:px-5 py-2.5 flex items-center gap-3 flex-wrap ${
                          it.tip
                            ? "bg-[#FEF3C7] border-l-4 border-[#F59E0B]"
                            : ""
                        }`}
                        data-testid={`iss-row-${sec.title}-${it.name}`}
                      >
                        <div className="flex-1 min-w-[200px] flex items-center gap-2">
                          {it.tip && (
                            <span
                              title="Commonly added on most jobs"
                              data-testid={`iss-tip-${sec.title}-${it.name}`}
                              className="flex-shrink-0"
                            >
                              <Lightbulb className="w-4 h-4 text-[#D97706] fill-[#FBBF24]" />
                            </span>
                          )}
                          <div className={`text-sm ${it.tip ? "font-semibold text-[#78350F]" : "text-[var(--ink)]"}`}>
                            {it.name}
                          </div>
                        </div>
                        <div className="w-16 text-right">
                          <input
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step={it.unit === "ea" || it.unit === "pr" ? "1" : "0.5"}
                            value={ln ? (qty || "") : ""}
                            placeholder="0"
                            className="input num h-9 text-sm text-center w-full"
                            onChange={(e) => setQty(sec.title, it.name, it.unit, effectivePrice, e.target.value)}
                            data-testid={`iss-qty-${sec.title}-${it.name}`}
                          />
                        </div>
                        <div className="w-12 text-center text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold">
                          {it.unit}
                        </div>
                        {isMiscMat ? (
                          <div className="w-24 text-right relative">
                            <input
                              type="number"
                              inputMode="decimal"
                              min="0"
                              step="0.01"
                              value={effectivePrice}
                              className={`input num h-9 text-sm w-full text-right ${
                                Number(effectivePrice) !== Number(it.price || 0)
                                  ? "border-[var(--brand)] bg-orange-50"
                                  : ""
                              }`}
                              onChange={(e) =>
                                setQty(sec.title, it.name, it.unit, Number(e.target.value) || 0, qty)
                              }
                              data-testid={`iss-mat-${sec.title}-${it.name}`}
                              title="Per-unit material + labor cost — overrides the catalog default for this estimate only"
                            />
                            {Number(effectivePrice) !== Number(it.price || 0) && (
                              <button
                                type="button"
                                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[var(--brand)] text-[var(--on-brand)] text-[10px] leading-none flex items-center justify-center"
                                onClick={() =>
                                  setQty(sec.title, it.name, it.unit, Number(it.price || 0), qty)
                                }
                                title={`Reset to catalog default ($${it.price})`}
                                data-testid={`iss-reset-mat-${sec.title}-${it.name}`}
                              >
                                ↺
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="w-24 text-right font-mono-num text-sm text-[var(--ink-2)]">
                            {fmt(it.price)}
                          </div>
                        )}
                        <div className="w-28 text-right font-mono-num text-base font-bold text-[var(--ink)]" data-testid={`iss-linetotal-${sec.title}-${it.name}`}>
                          {fmt(lineTotal)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}

        {/* Summary — bottom of the editor, mirrors the Siding estimator's
            position. No Tax field per supplier request. */}
        <section className="card p-6 mt-4" data-testid="iss-totals-summary">
          <div className="section-tag mb-4 flex items-center gap-2">
            <span>Summary</span>
            <span
              className="text-[10px] font-bold px-2 py-0.5 bg-orange-50 border border-[var(--brand)] text-[var(--brand-text)]"
              data-testid="iss-summary-tab-badge"
            >
              ISS Quote
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            <Stat label="Subtotal" val={fmt(totals.subTotal)} testid="iss-subtotal" />
            <Stat
              label={`Cut waste inside Material (${totals.subMat > 0 ? Math.round((totals.wasteAdd / totals.subMat) * 100) : 0}% of Material)`}
              val={fmt(totals.wasteAdd)}
              testid="iss-waste-add"
            />
            <Stat label="Base Cost" val={fmt(totals.base)} testid="iss-base-cost" bold />
            <Stat
              label={`Profit (${est.margin_pct || 0}% ${(est.pricing_mode || "margin")})`}
              val={fmt(totals.profit)}
              testid="iss-profit"
            />
            <Stat label="Sell Price" val={fmt(totals.sell)} testid="iss-sell-price" orange />
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              className="btn-primary"
              onClick={() => flush({ force: true })}
              disabled={saving}
              data-testid="iss-summary-save-btn"
            >
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
            </button>
            <button
              className="btn-secondary"
              onClick={handleOpenQuote}
              data-testid="iss-summary-quote-btn"
            >
              <FileText className="w-4 h-4" /> Customer Quote
            </button>
            <button
              className="btn-secondary"
              onClick={handlePrintMaterials}
              data-testid="iss-summary-materials-btn"
            >
              <ClipboardList className="w-4 h-4" /> Material List
            </button>
            <button
              className="btn-secondary"
              onClick={() => window.print()}
              data-testid="iss-summary-print-btn"
            >
              <Printer className="w-4 h-4" /> Print
            </button>
            <button
              className="btn-secondary"
              onClick={handleExportCsv}
              data-testid="iss-summary-export-csv-btn"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </section>
      </div>

      {showQuote && (
        <QuoteModal
          estimate={est}
          totals={{
            subMat: totals.subTotal,
            subLab: 0,
            wasted: totals.subTotal + totals.wasteAdd,
            tax: 0,
            base: totals.base,
            sell: totals.sell,
          }}
          onClose={() => setShowQuote(false)}
          emailConfigured={true}
          onEmail={async ({ recipient_email, html, subject, accept_token }) => {
            try {
              await api.post(`/estimates/${id}/email`, {
                recipient_email,
                html_quote: html,
                subject,
                accept_token,
              });
              toast.success("Quote sent");
              // Iter 79j.47 — Two-way sync. Persist a newly-typed
              // recipient back to the estimate so it prefills next time.
              const sent = (recipient_email || "").trim();
              const stored = (est?.customer_email || "").trim();
              if (sent && sent !== stored) updateField("customer_email", sent);
              return true;
            } catch (e) {
              toast.error(formatApiError(e.response?.data?.detail));
              return false;
            }
          }}
        />
      )}
    </main>
  );
}

function Stat({ label, val, orange, bold, testid }) {
  return (
    <div className="border-l-2 border-[var(--border)] pl-3" data-testid={testid ? `${testid}-card` : undefined}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] font-bold">{label}</div>
      <div
        className={`font-mono-num mt-1 ${
          orange
            ? "text-2xl font-bold text-[var(--brand-text)]"
            : bold
            ? "text-lg font-bold text-[var(--ink)]"
            : "text-base text-[var(--ink)]"
        }`}
        data-testid={testid}
      >
        {val}
      </div>
    </div>
  );
}
